/* eslint-disable prettier/prettier */
import { useState, useCallback, useEffect } from 'react';
import {
	resourceService,
	CreateResourceWithFilePayload,
	UpdateResourcePayload,
} from '../services/resourceService';
import { Resource } from '../types/resource';

// Hook for fetching resources (e.g., for a course library)
export const useResources = (
	context: 'course' | 'lesson' | 'all',
	id?: string
) => {
	const [resources, setResources] = useState<Resource[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const fetchResources = useCallback(async () => {
		setIsLoading(true);
		try {
			let data: Resource[];
			if (context === 'course' && id) {
				data = await resourceService.getResourcesByCourse(id);
			} else if (context === 'lesson' && id) {
				data = await resourceService.getResourcesByLesson(id);
			} else {
				data = await resourceService.getResourcesByCourse(id || ''); // Fallback to course resources if no context
			}
			setResources(data);
		} catch (err: any) {
			setError(err.message);
		} finally {
			setIsLoading(false);
		}
	}, [context, id]);

	useEffect(() => {
		fetchResources();
	}, [fetchResources]);

	return { resources, isLoading, error, refetch: fetchResources };
};

// Hook for Resource Actions (Upload, Download, Delete)
export const useResourceActions = () => {
	const [isProcessing, setIsProcessing] = useState(false);

	const download = async (resourceId: string) => {
		setIsProcessing(true);
		try {
			await resourceService.downloadResource(resourceId);
		} finally {
			setIsProcessing(false);
		}
	};

	const remove = async (resourceId: string) => {
		setIsProcessing(true);
		try {
			await resourceService.deleteResource(resourceId);
			return true;
		} finally {
			setIsProcessing(false);
		}
	};

	return { download, remove, isProcessing };
};

// 🔹 Upload Hook (SEPARATE)
export const useUploadResource = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	type UploadResult = {
		success: boolean;
		data?: any;
		message?: string;
	};

	const uploadResource = async (
		file: File,
		payload: CreateResourceWithFilePayload,
		onProgress?: (percent: number) => void
	): Promise<UploadResult> => {
		try {
			setLoading(true);
			setError(null);
			setSuccess(null);

			const response = await resourceService.uploadResourceWithFile(
				file,
				payload,
				onProgress
			);

			if (response.success) {
				const msg =
					response.message || 'Resource uploaded successfully';
				setSuccess(msg);
				return { success: true, data: response.data, message: msg };
			} else {
				const msg = response.message || 'Upload failed';
				setError(msg);
				return { success: false, data: null, message: msg };
			}
		} catch (err: any) {
			const msg = err?.response?.data?.message || 'Something went wrong';
			setError(msg);
			return { success: false, data: null, message: msg };
		} finally {
			setLoading(false);
		}
	};

	return {
		uploadResource,
		loading,
		error,
		success,
	};
};

// 🔹 Update Hook (SEPARATE)
export const useResource = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const updateResource = async (
		resourceId: string,
		data: UpdateResourcePayload
	) => {
		try {
			setLoading(true);
			setError(null);

			const response = await resourceService.updateResource(
				resourceId,
				data
			);

			if (!response.success) {
				setError(response.message);
				return { success: false, message: response.message };
			}

			return { success: true, message: response.message };
		} catch (err: any) {
			const message =
				err?.response?.data?.message ||
				err.message ||
				'Something went wrong';

			setError(message);

			return { success: false, message };
		} finally {
			setLoading(false);
		}
	};

	return {
		updateResource,
		loading,
		error,
	};
};
